
import { computed, defineComponent, nextTick, ref } from "vue";
import { debounce } from "lodash";
import {
  IKhoResponseBase,
  IPhieuNhapKhoResponseBase,
  IPhieuXuatKhoResponseBase,
  IVatTuTrongKhoResponseBase,
} from "@/core/interfaces/ApiResponses";
import { KhoService } from "@/core/services/Kho.service";
import {
  setBreadcrumbLinks,
  setCurrentPageTitle,
} from "@/core/helpers/breadcrumb";
import { useRoute, useRouter } from "vue-router";
import { useRouterCustom } from "@/core/hooks/use-router-custom";
import { getErrorMsg, swalNotification } from "@/core/helpers/utils";
import { useLoading } from "@/core/hooks/use-loading";
import { usePagination } from "@/core/hooks/use-pagination";
import { VatTuService } from "@/core/services/VatTu.service";
import { useButtonCustom } from "@/core/hooks/use-button-custom";
import { PhieuNhapKhoService } from "@/core/services/PhieuNhapKho.service";
import { AddVatTuItemsFor } from "@/core/enums/addVatTuItemsFor.enum";
import moment from "moment";
import ButtonCustom from "@/components/shared/buttons/ButtonCustom.vue";
import PageContentBase from "@/layout/_shared/PageContentBase.vue";
import PageContentSection from "@/layout/_shared/PageContentSection.vue";
import PageHeaderActions from "@/layout/header/PageHeaderActions.vue";
import PageHeaderActionItem from "@/layout/header/PageHeaderActionItem.vue";
import ConfirmationDialog from "@/components/confirmation-dialog/ConfirmationDialog.vue";
import AddVatTuItemV2 from "@/components/add-vat-tu-items-v2/AddVatTuItemV2.vue";
import VatTuKhoTable from "./vat-tu-kho-table/VatTuKhoTable.vue";
import PhieuNhapKhoTableV2 from "../phieu-nhap-kho/phieu-nhap-kho-table/PhieuNhapKhoTableV2.vue";
import { openPhieuNhapKhoPdf } from "@/components/shared/pdf-viewer/open-phieu-nhap-kho-pdf";
import Pagination from "@/components/shared/pagination/Pagination.vue";
import LabelCustom from "@/components/shared/label-custom/LabelCustom.vue";
import PhieuXuatKhoService from "@/core/services/Receipt.service";
import PhieuXuatKhoTable from "@/components/phieu-xuat-kho/PhieuXuatKhoTable.vue";
import DmvtTkGroupped from "@/components/shared/dmvt-tk-groupped/DmvtTkGroupped.vue";
import FilterPhieuNhapXuat from "@/components/filter-phieu-nhap-xuat/FilterPhieuNhapXuat.vue";
import { useFilterPhieuNhapXuat } from "@/core/hooks/useFilterPhieuNhapXuat";
import { PhieuNhapXuatStatusEnum } from "@/core/enums/phieuNhapXuatStatus.enum";
import FilterContainer from "@/layout/_shared/FilterContainer.vue";
import FilterItem from "@/layout/_shared/FilterItem.vue";
import Can from "@/components/Can.vue";
import {
  KHO,
  PHIEU_NHAP_KHO,
  PHIEU_XUAT_KHO,
} from "@/core/constants/permissions";
import DetailTopicLottie from "@/components/shared/detail-topic-lottie/DetailTopicLottie.vue";
import { faPencil } from "@fortawesome/free-solid-svg-icons";
import { faTrashCan as faTrash } from "@fortawesome/free-regular-svg-icons";

export default defineComponent({
  name: "kho-detail",

  components: {
    DetailTopicLottie,
    FilterContainer,
    FilterItem,
    ButtonCustom,
    Can,
    FilterPhieuNhapXuat,
    PageContentBase,
    PageContentSection,
    PageHeaderActions,
    PageHeaderActionItem,
    ConfirmationDialog,
    AddVatTuItemV2,
    LabelCustom,
    VatTuKhoTable,
    PhieuNhapKhoTableV2,
    Pagination,
    PhieuXuatKhoTable,
    DmvtTkGroupped,
  },

  setup() {
    const route = useRoute();
    const router = useRouter();
    const { goBack, push, replace } = useRouterCustom();
    const warehouseId = computed(() => Number(route.params.id) || 0);
    const { startLoading, endLoading } = useLoading();
    const {
      currentPage: currentPageVatTu,
      totalItems: totalVatTuItems,
      perPage: perPageVatTu,
      perPageArr: perPageArrVatTu,
      loading: loadingVatTu,
      searchTerm,
    } = usePagination();

    const {
      currentPage: currentPagePhieuNhap,
      totalItems: totalPhieuNhapItems,
      perPage: perPagePhieuNhap,
      perPageArr: perPageArrPhieuNhap,
      loading: loadingPhieuNhap,
      searchTerm: searchTermPhieuNhap,
    } = usePagination();

    const {
      currentPage: currentPagePhieuXuat,
      totalItems: totalPhieuXuatItems,
      perPage: perPagePhieuXuat,
      perPageArr: perPageArrPhieuXuat,
      loading: loadingPhieuXuat,
      searchTerm: searchTermPhieuXuat,
    } = usePagination();

    const { ButtonsType, ButtonTypeColors } = useButtonCustom();

    const departmentName = ref("");
    const warehouseDetail = ref<IKhoResponseBase | null>(null);
    const phieuNhapKhoItems = ref<IPhieuNhapKhoResponseBase[]>([]);
    const phieuXuatKhoItems = ref<IPhieuXuatKhoResponseBase[]>([]);

    const {
      processStatusFilter: processStatusFilterNhap,
      loaiPhieuFilter: loaiPhieuFilterNhap,
    } = useFilterPhieuNhapXuat({
      loaiPhieu: "nhap",
    });

    const {
      processStatusFilter: processStatusFilterXuat,
      loaiPhieuFilter: loaiPhieuFilterXuat,
    } = useFilterPhieuNhapXuat({
      loaiPhieu: "xuat",
    });

    const thuHoiFilter = ref(false);
    const congTyDuyetFilter = ref(false);

    const fetchWarehouseDetail = async () => {
      try {
        const {
          data: { data: warehouseRes },
        } = await KhoService.get(warehouseId.value);

        warehouseDetail.value = { ...warehouseRes };

        departmentName.value = warehouseDetail.value.don_vi.ten_don_vi;

        document.title = `Kho ${warehouseDetail.value.ten_kho} - Đường sắt Hà Nội`;
      } catch (e) {
        endLoading();

        await swalNotification(
          getErrorMsg(
            e,
            "Xin lỗi, có vẻ có lỗi xảy khi lấy thông tin kho, mời bạn thực hiện lại"
          ),
          "error"
        );
      }
    };

    const goToCreatePhieuTamNhap = () => {
      router.push({
        name: "tao-phieu-nhap-kho-khac",
        query: {
          idKho: String(warehouseId.value),
        },
      });
    };

    return {
      KHO,
      PHIEU_NHAP_KHO,
      PHIEU_XUAT_KHO,
      warehouseId,
      warehouseDetail,
      phieuNhapKhoItems,
      phieuXuatKhoItems,
      departmentName,
      formatDate: computed(() => moment),
      fetchWarehouseDetail,
      goToCreatePhieuTamNhap,
      goBack,
      push,
      replace,
      startLoading,
      endLoading,
      currentPageVatTu,
      totalVatTuItems,
      perPageVatTu,
      perPageArrVatTu,
      loadingVatTu,
      searchTerm,
      currentPagePhieuNhap,
      totalPhieuNhapItems,
      perPagePhieuNhap,
      perPageArrPhieuNhap,
      loadingPhieuNhap,
      searchTermPhieuNhap,
      currentPagePhieuXuat,
      totalPhieuXuatItems,
      perPagePhieuXuat,
      perPageArrPhieuXuat,
      loadingPhieuXuat,
      searchTermPhieuXuat,
      ButtonsType,
      ButtonTypeColors,
      processStatusFilterNhap,
      processStatusFilterXuat,
      loaiPhieuFilterNhap,
      loaiPhieuFilterXuat,
      thuHoiFilter,
      congTyDuyetFilter,
      faPencil,
      faTrash,
    };
  },

  data: () => ({
    AddVatTuItemsFor,
    idPhieuNhapDetail: null as null | number,
    isDeleteKhoConfirmationDialogDisplay: false,
    isDeletingModalDisplay: false,
    isAddVatTuModalDisplay: false,
    vatTuItemsDisplaying: [] as IVatTuTrongKhoResponseBase[],
    deletingVatTu: null as IVatTuTrongKhoResponseBase | null,
  }),

  watch: {
    async currentPageVatTu() {
      this.startLoading();
      await this.fetchVatTuKhoItems();
      this.endLoading();
    },

    async perPageVatTu() {
      if (this.currentPageVatTu === 1) {
        this.startLoading();
        await this.fetchVatTuKhoItems();
        this.endLoading();
        return;
      }

      this.currentPageVatTu = 1;
    },

    async currentPagePhieuNhap() {
      this.startLoading();
      await this.fetchPhieuNhapItems();
      this.endLoading();
    },

    async perPagePhieuNhap() {
      nextTick().then(async () => {
        if (this.currentPagePhieuNhap === 1) {
          this.startLoading();

          await this.fetchPhieuNhapItems();

          this.endLoading();

          return;
        }

        this.currentPagePhieuNhap = 1;
      });
    },

    async currentPagePhieuXuat() {
      this.startLoading();

      await this.fetchPhieuXuatItems();

      this.endLoading();
    },

    async perPagePhieuXuat() {
      nextTick().then(async () => {
        if (this.currentPagePhieuXuat === 1) {
          this.startLoading();

          await this.fetchPhieuXuatItems();

          this.endLoading();

          return;
        }

        this.currentPagePhieuXuat = 1;
      });
    },

    async loaiPhieuFilterNhap() {
      nextTick().then(async () => {
        if (this.currentPagePhieuNhap === 1) {
          this.startLoading();

          await this.fetchPhieuNhapItems();

          this.endLoading();

          return;
        }

        this.currentPagePhieuNhap = 1;
      });
    },

    async processStatusFilterNhap() {
      nextTick().then(async () => {
        if (this.currentPagePhieuNhap === 1) {
          this.startLoading();

          await this.fetchPhieuNhapItems();

          this.endLoading();

          return;
        }

        this.currentPagePhieuNhap = 1;
      });
    },

    async loaiPhieuFilterXuat() {
      nextTick().then(async () => {
        if (this.currentPagePhieuXuat === 1) {
          this.startLoading();

          await this.fetchPhieuXuatItems();

          this.endLoading();

          return;
        }

        this.currentPagePhieuXuat = 1;
      });
    },

    async processStatusFilterXuat() {
      nextTick().then(async () => {
        if (this.currentPagePhieuXuat === 1) {
          this.startLoading();

          await this.fetchPhieuXuatItems();

          this.endLoading();

          return;
        }

        this.currentPagePhieuXuat = 1;
      });
    },

    async thuHoiFilter() {
      nextTick().then(async () => {
        if (this.currentPageVatTu === 1) {
          this.startLoading();
          await this.fetchVatTuKhoItems();
          this.endLoading();
          return;
        }

        this.currentPageVatTu = 1;
      });
    },

    async congTyDuyetFilter() {
      nextTick().then(async () => {
        if (this.currentPageVatTu === 1) {
          this.startLoading();
          await this.fetchVatTuKhoItems();
          this.endLoading();
          return;
        }

        this.currentPageVatTu = 1;
      });
    },
  },

  async mounted() {
    setBreadcrumbLinks([
      {
        text: "Kho",
        routeData: {
          name: "danh-sach-kho",
        },
      },
      {
        text: "Chi tiết",
      },
    ]);

    this.startLoading();

    const promiseArray = [
      this.fetchWarehouseDetail(),
      this.fetchVatTuKhoItems(),
      this.fetchPhieuNhapItems(),
      this.fetchPhieuXuatItems(),
    ];

    await Promise.all(promiseArray);

    this.idPhieuNhapDetail = +this.$route.query["chi-tiet-phieu-nhap"];

    if (this.idPhieuNhapDetail) {
      openPhieuNhapKhoPdf(this.$router, this.idPhieuNhapDetail);
    }

    setCurrentPageTitle(`${this.warehouseDetail?.ten_kho}`);

    this.endLoading();
  },

  computed: {
    vatTuKhoComputed(): IVatTuTrongKhoResponseBase[] {
      return this.vatTuItemsDisplaying.map((vatTu) => ({
        ...vatTu,
        pivot: {
          don_gia: vatTu.pivot.don_gia,
          so_luong: vatTu.pivot.so_luong,
          id_kho: this.warehouseId,
          id_vat_tu: vatTu.id,
        },
      }));
    },
  },

  methods: {
    onDeleteVatTuButtonClicked({
      vatTu,
    }: {
      vatTu: IVatTuTrongKhoResponseBase;
    }) {
      this.isDeletingModalDisplay = true;

      this.deletingVatTu = vatTu;
    },

    async fetchVatTuKhoItems() {
      try {
        const {
          data: {
            data: { data: items, total },
          },
        } = await VatTuService.getVatTuItemsByKhoId({
          idKho: this.warehouseId,
          page: this.currentPageVatTu,
          perPage: this.perPageVatTu,
          searchTerm: this.searchTerm,
          ...(this.thuHoiFilter && { thu_hoi: 1 }),
          ...(this.congTyDuyetFilter && { cong_ty_duyet: 1 }),
        });

        this.vatTuItemsDisplaying = items;

        this.totalVatTuItems = total;
      } catch (e) {
        this.endLoading();

        await swalNotification(
          getErrorMsg(e, "Có lỗi xảy ra, không thể lấy danh sách vật tư"),
          "error"
        );
      }
    },

    async fetchPhieuNhapItems() {
      try {
        const {
          data: {
            data: { data: items, total },
          },
        } = await PhieuNhapKhoService.getListByKhoId({
          id_kho: this.warehouseId,
          current_page: this.currentPagePhieuNhap,
          per_page: this.perPagePhieuNhap,
          search: this.searchTermPhieuNhap,
          ...(this.loaiPhieuFilterNhap !== "" && {
            loai_phieu: this.loaiPhieuFilterNhap,
          }),
          ...(this.processStatusFilterNhap !== "" && {
            processing_status: this.processStatusFilterNhap,
          }),
        });

        this.phieuNhapKhoItems = items;

        this.totalPhieuNhapItems = total;
      } catch (e) {
        this.endLoading();

        await swalNotification(
          getErrorMsg(
            e,
            "Có lỗi xảy ra, không thể lấy danh sách phiếu nhập kho"
          ),
          "error"
        );
      }
    },

    async fetchPhieuXuatItems() {
      try {
        const {
          data: {
            data: { data: items, total },
          },
        } = await PhieuXuatKhoService.getListByKhoId({
          id_kho: this.warehouseId,
          current_page: this.currentPagePhieuXuat,
          per_page: this.perPagePhieuXuat,
          search: this.searchTermPhieuXuat,
          ...(this.loaiPhieuFilterXuat !== "" && {
            loai_phieu: this.loaiPhieuFilterXuat,
          }),
          ...(this.processStatusFilterXuat !== "" && {
            processing_status: this.processStatusFilterXuat,
          }),
        });

        this.phieuXuatKhoItems = items;
        this.totalPhieuXuatItems = total;
      } catch (e) {
        this.endLoading();
        await swalNotification(
          getErrorMsg(
            e,
            "Có lỗi xảy ra, không thể lấy danh sách phiếu xuất kho kho"
          ),
          "error"
        );
      }
    },

    onPhieuNhapDetailButtonClicked({
      phieuNhapKhoData,
    }: {
      phieuNhapKhoData: IPhieuNhapKhoResponseBase;
    }) {
      openPhieuNhapKhoPdf(this.$router, phieuNhapKhoData.id);
    },

    onAddVatTuButtonClicked() {
      this.isAddVatTuModalDisplay = true;
    },

    async onAddedVatTuItems() {
      this.isAddVatTuModalDisplay = false;

      this.startLoading();

      await this.fetchWarehouseDetail();

      this.endLoading();
    },

    async confirmDeleteKho() {
      this.isDeleteKhoConfirmationDialogDisplay = false;

      try {
        this.startLoading();

        await KhoService.delete(this.warehouseId);

        this.endLoading();

        await swalNotification("Xóa thành công");

        this.push({ name: "danh-sach-kho" });
      } catch (e) {
        this.endLoading();

        await swalNotification(
          getErrorMsg(e, "Có lỗi xảy ra khi xóa kho này"),
          "error"
        );
      }
    },

    async confirmDelete() {
      try {
        this.startLoading();

        await KhoService.removeVatTuKho({
          id_kho: this.warehouseId,
          id_vat_tu: this.deletingVatTu?.id || 0,
        });

        this.endLoading();

        this.isDeletingModalDisplay = false;

        await swalNotification("Xóa thành công", "success");

        await this.fetchWarehouseDetail();

        this.endLoading();

        this.deletingVatTu = null;
      } catch (e) {
        this.endLoading();

        this.isDeletingModalDisplay = false;

        await swalNotification(
          getErrorMsg(e, "Có lỗi xảy ra, không thể xóa vật tư này"),
          "error"
        );
      }
    },

    async onVatTuIntoKhoAdded() {
      this.currentPageVatTu = 1;
      this.isAddVatTuModalDisplay = false;

      this.fetchVatTuKhoItems();
    },

    search: debounce(function () {
      this.fetchVatTuKhoItems();
    }, 500),

    searchPhieuNhap: debounce(function () {
      this.fetchPhieuNhapItems();
    }, 500),

    searchPhieuXuat: debounce(function () {
      this.fetchPhieuXuatItems();
    }, 500),

    updateFilterPhieuNhap({
      processStatusFilter,
      loaiPhieuFilter,
    }: {
      processStatusFilter: PhieuNhapXuatStatusEnum | "";
      loaiPhieuFilter: number | "";
    }) {
      this.loaiPhieuFilterNhap = loaiPhieuFilter;
      this.processStatusFilterNhap = processStatusFilter;
    },

    updateFilterPhieuXuat({
      processStatusFilter,
      loaiPhieuFilter,
    }: {
      processStatusFilter: PhieuNhapXuatStatusEnum | "";
      loaiPhieuFilter: number | "";
    }) {
      this.loaiPhieuFilterXuat = loaiPhieuFilter;
      this.processStatusFilterXuat = processStatusFilter;
    },
  },
});
